import React, { useState, useEffect } from 'react';
import { X, Copy, AlertCircle, CheckCircle, Edit3 } from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import { LowCtrOpportunity, rewriteTitle, applyTitleRewrite, TitleRewriteResponse } from '../../services/api';
import './TitleRewriteModal.css';

interface TitleRewriteModalProps {
    projectId: string;
    opportunity: LowCtrOpportunity;
    onClose: () => void;
}

type RewriteState = 'loading' | 'done' | 'error';
type ApplyState = 'idle' | 'applying' | 'done' | 'error';
type CopyState = 'idle' | 'copied';

const TitleRewriteModal: React.FC<TitleRewriteModalProps> = ({ projectId, opportunity, onClose }) => {
    const [rewriteState, setRewriteState] = useState<RewriteState>('loading');
    const [rewriteData, setRewriteData] = useState<TitleRewriteResponse | null>(null);
    const [rewriteError, setRewriteError] = useState<string | null>(null);

    // Editable title — starts as Gemini's rewrite
    const [editedTitle, setEditedTitle] = useState('');

    const [applyState, setApplyState] = useState<ApplyState>('idle');
    const [copyState, setCopyState] = useState<CopyState>('idle');

    useEffect(() => {
        let cancelled = false;
        const fetchRewrite = async () => {
            setRewriteState('loading');
            setRewriteError(null);
            try {
                const result = await rewriteTitle(projectId, {
                    page: opportunity.page,
                    top_query: opportunity.top_query,
                    task_id: opportunity.task_id,
                });
                if (!cancelled) {
                    setRewriteData(result);
                    setEditedTitle(result.rewritten_title);
                    setRewriteState('done');
                }
            } catch (err: any) {
                if (!cancelled) {
                    const detail = err?.response?.data?.detail;
                    setRewriteError(typeof detail === 'string' ? detail : 'Failed to generate title rewrite.');
                    setRewriteState('error');
                }
            }
        };
        fetchRewrite();
        return () => { cancelled = true; };
    }, [projectId, opportunity.page, opportunity.top_query, opportunity.task_id]);

    const canApply =
        rewriteData?.apply_target?.type === 'task' ||
        rewriteData?.apply_target?.type === 'landing_page';

    const handleApply = async () => {
        if (!rewriteData || !canApply) return;
        setApplyState('applying');
        try {
            await applyTitleRewrite(projectId, rewriteData.apply_target, editedTitle);
            setApplyState('done');
        } catch (err: any) {
            setApplyState('error');
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(editedTitle).then(() => {
            setCopyState('copied');
            setTimeout(() => setCopyState('idle'), 2000);
        });
    };

    // Close on backdrop click
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="trm-overlay" onClick={handleBackdropClick}>
            <div className="trm-modal" role="dialog" aria-modal="true" aria-label="Rewrite page title">
                <GlassCard className="trm-card">
                    {/* Header */}
                    <div className="trm-header">
                        <div className="trm-header-title">
                            <Edit3 size={16} />
                            <span>Rewrite Page Title</span>
                        </div>
                        <GlassButton
                            variant="ghost"
                            size="sm"
                            icon={<X size={16} />}
                            onClick={onClose}
                            title="Close"
                        />
                    </div>

                    <p className="trm-page-url" title={opportunity.page}>
                        {opportunity.page}
                    </p>

                    {/* Loading */}
                    {rewriteState === 'loading' && (
                        <div className="trm-loading">
                            <div className="trm-spinner" />
                            <span>Fetching competitor titles and generating rewrite…</span>
                        </div>
                    )}

                    {/* Error */}
                    {rewriteState === 'error' && (
                        <div className="trm-error">
                            <AlertCircle size={16} />
                            <span>{rewriteError || 'Failed to generate — please try again.'}</span>
                        </div>
                    )}

                    {/* Done */}
                    {rewriteState === 'done' && rewriteData && (
                        <>
                            {/* Current title */}
                            <div className="trm-field">
                                <div className="trm-field-label">Current title</div>
                                <div className="trm-current-title">{rewriteData.current_title || '(none)'}</div>
                            </div>

                            {/* Competitor titles */}
                            {rewriteData.competitor_titles.length > 0 ? (
                                <div className="trm-field">
                                    <div className="trm-field-label">Top-3 competitors (currently outranking this page)</div>
                                    <ol className="trm-competitor-list">
                                        {rewriteData.competitor_titles.map((t, i) => (
                                            <li key={i}>{t}</li>
                                        ))}
                                    </ol>
                                </div>
                            ) : (
                                <div className="trm-no-serp">
                                    <AlertCircle size={13} />
                                    <span>No competitor data available — best-effort rewrite only.</span>
                                </div>
                            )}

                            {/* Low-confidence badge */}
                            {rewriteData.low_confidence && (
                                <div className="trm-low-confidence">
                                    <AlertCircle size={13} />
                                    <span>Low confidence — limited grounding data. Review before applying.</span>
                                </div>
                            )}

                            {/* Rewritten title (editable) */}
                            <div className="trm-field">
                                <div className="trm-field-label">
                                    Rewritten title
                                    <span className="trm-char-count">
                                        {editedTitle.length}/60 chars
                                    </span>
                                </div>
                                <input
                                    className="trm-title-input"
                                    type="text"
                                    value={editedTitle}
                                    onChange={e => setEditedTitle(e.target.value)}
                                    maxLength={100}
                                    placeholder="Edit the rewritten title…"
                                />
                            </div>

                            {/* Rationale */}
                            {rewriteData.rationale && (
                                <div className="trm-rationale">
                                    <span className="trm-rationale-label">Rationale: </span>
                                    {rewriteData.rationale}
                                </div>
                            )}

                            {/* No writable target explanation */}
                            {!canApply && (
                                <div className="trm-no-apply">
                                    AIME doesn't own this page's metadata — copy and update it where it's hosted.
                                </div>
                            )}

                            {/* Actions */}
                            <div className="trm-actions">
                                {canApply ? (
                                    <>
                                        {applyState === 'done' ? (
                                            <span className="trm-applied">
                                                <CheckCircle size={14} />
                                                Title updated
                                            </span>
                                        ) : (
                                            <GlassButton
                                                variant="primary"
                                                pill
                                                disabled={applyState === 'applying' || !editedTitle.trim()}
                                                loading={applyState === 'applying'}
                                                onClick={handleApply}
                                            >
                                                {applyState === 'applying' ? 'Applying…' : 'Apply title'}
                                            </GlassButton>
                                        )}
                                        {applyState === 'error' && (
                                            <span className="trm-apply-error">
                                                <AlertCircle size={12} />
                                                Failed — try again
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <GlassButton
                                        variant="secondary"
                                        onClick={handleCopy}
                                        disabled={!editedTitle.trim()}
                                    >
                                        {copyState === 'copied' ? (
                                            <>
                                                <CheckCircle size={14} />
                                                Copied!
                                            </>
                                        ) : (
                                            <>
                                                <Copy size={14} />
                                                Copy title
                                            </>
                                        )}
                                    </GlassButton>
                                )}
                                <GlassButton variant="secondary" onClick={onClose}>
                                    Cancel
                                </GlassButton>
                            </div>
                        </>
                    )}
                </GlassCard>
            </div>
        </div>
    );
};

export default TitleRewriteModal;
